"use client";

import { memo, useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { DndContext, type DragEndEvent, closestCenter } from "@dnd-kit/core";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { SortableContext, arrayMove, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Icon } from "@/components/ui/icon";
import { Input, Textarea } from "@/components/ui/input";
import { Paper } from "@/components/ui/paper";
import { Switch } from "@/components/ui/switch";

import {
  DEFAULT_RUBRIC_LEVELS, EVIDENCE_LABELS, FIELD_TYPE_LABELS, RECOMMENDATION_OPTIONS,
  keyFromLabel, type Evidence, type FieldType, type RubricLevel, type ScorecardField,
} from "../../lib/types";
import { DragHandle, RowIconButton, ScorecardSelect, SectionCard } from "../shared";
import { useScorecardSensors } from "../sortable";

interface Props {
  value: ScorecardField[];
  onChange: (fields: ScorecardField[]) => void;
  errors: Record<string, string>;
  disabled?: boolean;
  readOnly?: boolean;
}

const TYPE_ORDER: FieldType[] = ["rubric_score", "text", "yes_no", "single_choice"];
const TYPE_OPTIONS = TYPE_ORDER.map((t) => ({ value: t, label: FIELD_TYPE_LABELS[t] }));
const EVIDENCE_ORDER = Object.keys(EVIDENCE_LABELS) as Evidence[];
const EVIDENCE_OPTIONS = EVIDENCE_ORDER.map((ev) => ({ value: ev, label: EVIDENCE_LABELS[ev] }));
// A rubric score always carries evidence, so "forbidden" isn't offered there.
const EVIDENCE_OPTIONS_SCORED = EVIDENCE_OPTIONS.filter((o) => o.value !== "forbidden");

/** React-key ids only — fall back when `crypto.randomUUID` needs a secure context. */
function newId(): string {
  const c = globalThis.crypto;
  if (c && typeof c.randomUUID === "function") return c.randomUUID();
  return Math.random().toString(36).slice(2) || Date.now().toString(36);
}

function nextFieldKey(fields: ScorecardField[]): string {
  const used = new Set(fields.map((f) => f.key));
  let n = 1;
  while (used.has(`field_${n}`)) n += 1;
  return `field_${n}`;
}

function syncIds(prev: string[], length: number): string[] {
  if (prev.length === length) return prev;
  if (prev.length < length) {
    return [...prev, ...Array.from({ length: length - prev.length }, () => newId())];
  }
  return prev.slice(0, length);
}

/** Drop blank / whitespace-only lines on blur so Enter can start a new option while typing. */
function parseOptions(raw: string): string[] {
  return raw
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);
}

export function ScorecardFieldEditor({ value, onChange, errors, disabled, readOnly }: Props) {
  const locked = Boolean(disabled || readOnly);
  const [defaultLevels, setDefaultLevels] = useState<RubricLevel[]>(DEFAULT_RUBRIC_LEVELS);
  const [ids, setIds] = useState<string[]>(() => value.map(() => newId()));
  const [keyTouched, setKeyTouched] = useState<Set<string>>(new Set());
  const hasRec = value.some((f) => f.type === "overall_recommendation");
  const sensors = useScorecardSensors();

  if (ids.length !== value.length) {
    setIds(syncIds(ids, value.length));
  }

  const rowIds = syncIds(ids, value.length);

  const update = (i: number, patch: Partial<ScorecardField>) => {
    const next = value.map((f, idx) => (idx === i ? { ...f, ...patch } : f));
    onChange(next);
  };

  // The Type/Evidence pickers are Popover-backed `Select`s — an order of
  // magnitude pricier to render than the inputs around them. Typing a field
  // label re-renders this whole editor, so they're memoized on (index, value,
  // disabled) and driven by these index-taking callbacks, which stay
  // referentially stable by reading the latest fields through a ref.
  const latest = useRef({ value, onChange, defaultLevels });
  useEffect(() => {
    latest.current = { value, onChange, defaultLevels };
  });

  const changeTypeAt = useCallback((i: number, type: FieldType) => {
    const { value: fields, onChange: emit, defaultLevels: levels } = latest.current;
    const f = fields[i];
    if (!f) return;
    const patch: Partial<ScorecardField> = { type, levels: undefined, options: undefined };
    if (type === "rubric_score") {
      patch.levels = levels.map((l) => ({ ...l }));
      if (f.evidence === "forbidden") { patch.evidence = "required"; patch.autofill_allowed = true; }
    }
    if (type === "single_choice") patch.options = ["Option 1", "Option 2"];
    emit(fields.map((x, idx) => (idx === i ? { ...x, ...patch } : x)));
  }, []);

  const changeEvidenceAt = useCallback((i: number, evidence: Evidence) => {
    const { value: fields, onChange: emit } = latest.current;
    if (!fields[i]) return;
    const patch: Partial<ScorecardField> = {
      evidence,
      ...(evidence === "forbidden" ? { autofill_allowed: false } : {}),
    };
    emit(fields.map((x, idx) => (idx === i ? { ...x, ...patch } : x)));
  }, []);

  const markKeyTouched = (id: string) => {
    setKeyTouched((prev) => {
      if (prev.has(id)) return prev;
      const next = new Set(prev);
      next.add(id);
      return next;
    });
  };

  const addField = () => {
    setIds((prev) => [...syncIds(prev, value.length), newId()]);
    onChange([...value, { key: nextFieldKey(value), label: "", type: "text", required: false, evidence: "optional", autofill_allowed: true, help: null }]);
  };

  const addRecommendation = () => {
    setIds((prev) => [...syncIds(prev, value.length), newId()]);
    onChange([...value, {
      key: "overall_recommendation", label: "Overall recommendation", type: "overall_recommendation",
      options: [...RECOMMENDATION_OPTIONS], required: true, evidence: "forbidden", autofill_allowed: false, help: null,
    }]);
  };

  const onDragEnd = (e: DragEndEvent) => {
    if (!e.over || e.active.id === e.over.id) return;
    const from = rowIds.findIndex((id) => id === e.active.id);
    const to = rowIds.findIndex((id) => id === e.over!.id);
    if (from < 0 || to < 0) return;
    setIds(arrayMove(rowIds, from, to));
    onChange(arrayMove(value, from, to));
  };

  const removeField = (i: number) => {
    const id = rowIds[i];
    setIds(rowIds.filter((_, idx) => idx !== i));
    setKeyTouched((prev) => {
      if (!id || !prev.has(id)) return prev;
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
    onChange(value.filter((_, idx) => idx !== i));
  };

  return (
    <SectionCard
      iconName="IconBulletList"
      title="Fields"
      action={
        !readOnly ? (
          <Button
            type="button"
            variant="secondary"
            size="sm"
            disabled={disabled}
            onClick={addField}
            icon={<Icon name="IconPlusSmall" size={15} />}
          >
            Add field
          </Button>
        ) : null
      }
    >
      {errors._set && (
        <p role="alert" className="text-sm text-destructive">
          {errors._set}
        </p>
      )}

      {value.length === 0 && (
        <p className="rounded-md border border-dashed border-border px-3 py-6 text-center text-[13px] text-contrast/50">
          {readOnly ? "This type has no fields." : "No fields yet — add the first one interviewers should score."}
        </p>
      )}

      <DndContext sensors={sensors} collisionDetection={closestCenter} modifiers={[restrictToVerticalAxis]} onDragEnd={onDragEnd}>
        <SortableContext items={rowIds} strategy={verticalListSortingStrategy}>
          <ul className="flex flex-col gap-2">
            {value.map((f, i) => {
              const id = rowIds[i];
              const fieldLabel = f.label || `field ${i + 1}`;
              return (
                <SortableRow
                  key={id}
                  id={id}
                  dragLabel={`Drag to reorder ${fieldLabel}`}
                  disabled={disabled}
                  readOnly={readOnly}
                  onRemove={readOnly ? undefined : () => removeField(i)}
                  removeLabel="Remove"
                  badge={
                    f.type === "overall_recommendation" ? (
                      <Badge variant="outline-muted" size="xs" className="rounded-sm">
                        {FIELD_TYPE_LABELS.overall_recommendation}
                      </Badge>
                    ) : null
                  }
                  title={
                    <Input
                      aria-label="Label"
                      variant="modal"
                      size="sm"
                      placeholder="Label"
                      value={f.label}
                      disabled={locked}
                      onChange={(e) =>
                        f.type === "overall_recommendation"
                          ? update(i, { label: e.target.value })
                          : update(i, {
                              label: e.target.value,
                              ...(keyTouched.has(id) ? {} : { key: keyFromLabel(e.target.value) || f.key }),
                            })
                      }
                    />
                  }
                >
                  {f.type === "overall_recommendation" ? (
                    <>
                      <Paper.Description>
                        Strong hire · Hire · No hire · Strong no hire. Human-only. Evidence forbidden.
                      </Paper.Description>
                      <ToggleRow>
                        <CheckLabel
                          label="Required"
                          checked={f.required}
                          disabled={locked}
                          onChange={(v) => update(i, { required: v })}
                        />
                      </ToggleRow>
                    </>
                  ) : (
                    <>
                      <div className="grid gap-2 sm:grid-cols-3">
                        <Input
                          aria-label="Key"
                          variant="modal"
                          size="sm"
                          value={f.key}
                          disabled={locked}
                          onChange={(e) => { markKeyTouched(id); update(i, { key: e.target.value }); }}
                        />
                        <TypeSelect
                          index={i}
                          value={f.type}
                          disabled={locked}
                          onChangeType={changeTypeAt}
                        />
                        <EvidenceSelect
                          index={i}
                          value={f.evidence}
                          scored={f.type === "rubric_score"}
                          disabled={locked}
                          onChangeEvidence={changeEvidenceAt}
                        />
                      </div>
                      <ToggleRow>
                        <CheckLabel
                          label="Required"
                          checked={f.required}
                          disabled={locked}
                          onChange={(v) => update(i, { required: v })}
                        />
                        <SwitchLabel
                          label="Arbi may draft"
                          checked={f.autofill_allowed}
                          disabled={locked || f.evidence === "forbidden"}
                          onChange={(v) => update(i, { autofill_allowed: v })}
                        />
                      </ToggleRow>
                      {f.type === "rubric_score" && (
                        <LevelsEditor
                          title="Rubric levels"
                          value={f.levels ?? []}
                          onChange={(levels) => update(i, { levels })}
                          disabled={locked}
                          readOnly={readOnly}
                        />
                      )}
                      {f.type === "single_choice" && (
                        <div className="flex flex-col gap-1.5">
                          <span className="text-xs font-medium text-contrast/60">Options (one per line)</span>
                          <Textarea
                            aria-label="Options (one per line)"
                            variant="modal"
                            rows={3}
                            disabled={locked}
                            value={(f.options ?? []).join("\n")}
                            onChange={(e) => update(i, { options: e.target.value.split("\n") })}
                            onBlur={(e) => update(i, { options: parseOptions(e.target.value) })}
                          />
                        </div>
                      )}
                      <Input
                        aria-label="Help"
                        variant="modal"
                        size="sm"
                        placeholder="Help text (optional)"
                        value={f.help ?? ""}
                        disabled={locked}
                        onChange={(e) => update(i, { help: e.target.value || null })}
                      />
                    </>
                  )}
                  {errors[f.key] && (
                    <p role="alert" className="text-xs text-destructive">
                      {errors[f.key]}
                    </p>
                  )}
                </SortableRow>
              );
            })}
          </ul>
        </SortableContext>
      </DndContext>

      {!readOnly && !hasRec && (
        <div>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={disabled}
            onClick={addRecommendation}
            icon={<Icon name="IconPlusSmall" size={15} />}
          >
            Add recommendation
          </Button>
        </div>
      )}

      {!readOnly && (
        <div className="flex flex-col gap-2 rounded-lg border border-border bg-contrast/2 p-3">
          <div className="flex flex-col gap-0.5">
            <span className="text-sm font-medium text-heading">Default rubric levels</span>
            <span className="text-[13px] text-contrast/60">
              Pre-fills new rubric fields. Levels are stored per field.
            </span>
          </div>
          <LevelsEditor value={defaultLevels} onChange={setDefaultLevels} disabled={locked} />
          <div>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={disabled}
              onClick={() =>
                onChange(
                  value.map((f) =>
                    f.type === "rubric_score" ? { ...f, levels: defaultLevels.map((l) => ({ ...l })) } : f,
                  ),
                )
              }
            >
              Apply to all rubric fields
            </Button>
          </div>
        </div>
      )}
    </SectionCard>
  );
}

const TypeSelect = memo(function TypeSelect({
  index,
  value,
  disabled,
  onChangeType,
}: {
  index: number;
  value: FieldType;
  disabled?: boolean;
  onChangeType: (index: number, type: FieldType) => void;
}) {
  const onChange = useCallback(
    (type: FieldType) => onChangeType(index, type),
    [index, onChangeType],
  );
  return (
    <ScorecardSelect
      label="Type"
      value={value}
      options={TYPE_OPTIONS}
      disabled={disabled}
      variant="modal"
      onChange={onChange}
    />
  );
});

const EvidenceSelect = memo(function EvidenceSelect({
  index,
  value,
  scored,
  disabled,
  onChangeEvidence,
}: {
  index: number;
  value: Evidence;
  scored: boolean;
  disabled?: boolean;
  onChangeEvidence: (index: number, evidence: Evidence) => void;
}) {
  const onChange = useCallback(
    (evidence: Evidence) => onChangeEvidence(index, evidence),
    [index, onChangeEvidence],
  );
  return (
    <ScorecardSelect
      label="Evidence"
      value={value}
      options={scored ? EVIDENCE_OPTIONS_SCORED : EVIDENCE_OPTIONS}
      disabled={disabled}
      variant="modal"
      onChange={onChange}
    />
  );
});

function ToggleRow({ children }: { children: ReactNode }) {
  return <div className="flex flex-wrap items-center gap-x-4 gap-y-1">{children}</div>;
}

/**
 * `Checkbox`/`Switch` render a `<button role="checkbox|switch">`, which takes
 * its name from contents — a wrapping <label> alone leaves it anonymous — so
 * the visible text is mirrored into `aria-label`.
 */
function CheckLabel({
  label,
  checked,
  disabled,
  onChange,
}: {
  label: string;
  checked: boolean;
  disabled?: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label className="flex cursor-pointer items-center gap-2 text-[13px] text-contrast/70">
      <Checkbox
        aria-label={label}
        checked={checked}
        disabled={disabled}
        onChange={(v) => onChange(Boolean(v))}
      />
      {label}
    </label>
  );
}

function SwitchLabel({
  label,
  checked,
  disabled,
  onChange,
}: {
  label: string;
  checked: boolean;
  disabled?: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label className="flex cursor-pointer items-center gap-0.5 text-[13px] text-contrast/70">
      <Switch aria-label={label} checked={checked} disabled={disabled} onCheckedChange={onChange} />
      {label}
    </label>
  );
}

function LevelsEditor({
  title,
  value,
  onChange,
  disabled,
  readOnly,
}: {
  title?: string;
  value: RubricLevel[];
  onChange: (v: RubricLevel[]) => void;
  disabled?: boolean;
  readOnly?: boolean;
}) {
  const set = (i: number, patch: Partial<RubricLevel>) =>
    onChange(value.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));
  return (
    <div className="flex flex-col gap-1.5">
      {title && <span className="text-xs font-medium text-contrast/60">{title}</span>}
      {value.map((l, i) => (
        <div key={i} className="grid grid-cols-[4rem_1fr_auto] items-center gap-2">
          <Input
            aria-label={`Level ${i + 1} value`}
            variant="modal"
            size="sm"
            type="number"
            value={l.value}
            disabled={disabled}
            onChange={(e) => set(i, { value: Number(e.target.value) })}
          />
          <Input
            aria-label={`Level ${i + 1} anchor`}
            variant="modal"
            size="sm"
            placeholder="What this level means"
            value={l.anchor}
            disabled={disabled}
            onChange={(e) => set(i, { anchor: e.target.value })}
          />
          {!readOnly ? (
            <RowIconButton
              label={`Remove level ${i + 1}`}
              title="Remove level"
              disabled={disabled || value.length <= 2}
              onClick={() => onChange(value.filter((_, idx) => idx !== i))}
            >
              <Icon name="IconCrossMedium" size={14} aria-hidden="true" />
            </RowIconButton>
          ) : (
            <span />
          )}
        </div>
      ))}
      {!readOnly && (
        <div>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={disabled || value.length >= 7}
            onClick={() => onChange([...value, { value: (value.at(-1)?.value ?? 0) + 1, anchor: "" }])}
          >
            Add level
          </Button>
        </div>
      )}
    </div>
  );
}

/**
 * One field as a `Paper.Container` card — drag grabber + inline name on the
 * header row, the field's own controls stacked underneath.
 */
function SortableRow({
  id,
  dragLabel,
  disabled,
  readOnly,
  badge,
  title,
  onRemove,
  removeLabel,
  children,
}: {
  id: string;
  dragLabel: string;
  disabled?: boolean;
  readOnly?: boolean;
  badge?: ReactNode;
  title: ReactNode;
  onRemove?: () => void;
  removeLabel: string;
  children: ReactNode;
}) {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition } = useSortable({
    id,
    disabled: disabled || readOnly,
  });
  return (
    <li ref={setNodeRef} style={{ transform: CSS.Transform.toString(transform), transition }}>
      <Paper.Container className="flex-col items-stretch gap-2.5 md:flex-col md:items-stretch">
        <div className="flex items-center gap-1.5">
          {!readOnly && (
            <DragHandle
              ref={setActivatorNodeRef}
              label={dragLabel}
              title="Drag to reorder"
              disabled={disabled}
              {...attributes}
              {...listeners}
            />
          )}
          <div className="min-w-0 flex-1">{title}</div>
          {badge}
          {onRemove && (
            <RowIconButton label={removeLabel} title={removeLabel} disabled={disabled} onClick={onRemove}>
              <Icon name="IconTrashCanSimple" size={15} aria-hidden="true" />
            </RowIconButton>
          )}
        </div>
        {children}
      </Paper.Container>
    </li>
  );
}
